import {
  ContentBlocks_CTA,
  ContentBlocks_ImageVideo,
  ContentBlocks_TextBlock,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/content_blocks.types';
import {PromotionTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tracker.types';
import {
  TrackingPageTypeWithId,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk';
import {TrackPromotions} from '@aboutyou/tracking';
import {
  Button,
  FONT_SIZES,
  desktopAndUp,
  extraLargeScreens,
  mediaTabletAndUp,
  tabletAndUp,
} from '@aboutyou/ui-components';
import React, {useMemo, useState} from 'react';
import styled, {css} from 'styled-components';
import {HeroElementCollapsibleText} from './HeroElementCollapsibleText';
import {
  HeroElementBodyText,
  HeroElementHeadline,
  ImageOrVideoTile,
  VideoBlur,
  hexToRgb,
} from './shared';

interface HeroElementTileProps {
  headline?: ContentBlocks_TextBlock;
  text?: ContentBlocks_TextBlock;
  media: ContentBlocks_ImageVideo;
  backgroundColor: string;
  layout: 'desktop' | 'mobile';
  cta?: ContentBlocks_CTA;
  promotionTracker: PromotionTracker;
  streamPosition: number;
}

export const HeroElementTile: React.FC<HeroElementTileProps> = ({
  headline,
  text,
  cta,
  media,
  backgroundColor,
  layout,
  promotionTracker,
  streamPosition,
}) => {
  const [accordionOpen, setAccordionOpen] = useState(false);

  const textScaleFactor = useMemo(() => {
    if (!text || text.text.length >= 450) {
      return 1;
    }

    if (text.text.length <= 250) {
      return 1.5;
    }

    return 1.2;
  }, [text]);

  return (
    <StyledTrackPromotion
      streamPosition={streamPosition}
      promotion={{
        source: TrackingPromotionCreative.Teaser,
        heroElement: {
          ...promotionTracker,
          destination: cta?.link.url,
        },
      }}
      pageType={TrackingPageTypeWithId.Brand}
      recoList={{
        sectionIndex: 0,
        recoType: 'brands_hero_tile',
        elements: [],
      }}
      Comp="li"
    >
      <ContentWrapper>
        <ImageOrVideoTile
          media={media}
          videoControlsPosition={layout === 'mobile' ? 'top' : 'left-top'}
        />
        {layout === 'mobile' && accordionOpen && <VideoBlur />}
        <BodyWrapper
          $gradientColor={hexToRgb(backgroundColor)}
          $textScaleFactor={textScaleFactor}
        >
          {headline && (
            <StyledHeroElementHeadline
              color="White"
              $textScaleFactor={textScaleFactor}
            >
              {headline.text}
            </StyledHeroElementHeadline>
          )}

          {text && (
            <>
              {layout === 'mobile' ? (
                <HeroElementCollapsibleText
                  text={text.text}
                  isOpen={accordionOpen}
                  onToggle={isOpen => setAccordionOpen(isOpen)}
                />
              ) : (
                <StyledHeroElementBodyText
                  color="White"
                  $textScaleFactor={textScaleFactor}
                >
                  {text.text}
                </StyledHeroElementBodyText>
              )}
            </>
          )}
          {cta && (
            <StyledButton
              href={cta.link.url}
              $textScaleFactor={textScaleFactor}
              variant="glassy"
            >
              {cta.text.text}
            </StyledButton>
          )}
        </BodyWrapper>
      </ContentWrapper>
    </StyledTrackPromotion>
  );
};

const StyledTrackPromotion = styled(TrackPromotions)`
  padding-top: 1rem;
  padding-bottom: 3rem;
`;

const ContentWrapper = styled.div`
  position: relative;
  display: flex;
  margin: 0 -16px 6rem;

  ${tabletAndUp(css`
    flex-direction: row;
    margin: 0;
    border-radius: 0.25rem;
    overflow: hidden;
  `)}
`;

const BodyWrapper = styled.div<{
  $gradientColor: {
    r: number;
    g: number;
    b: number;
  };
  $textScaleFactor: number;
}>`
  width: 100%;
  position: absolute;
  bottom: -6rem;
  padding: 2rem 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  backdrop-filter: blur(2px);

  ${props => {
    const {r, g, b} = props.$gradientColor;
    return css`
      background-image: linear-gradient(
        to top,
        rgba(${r}, ${g}, ${b}, 1) 60%,
        rgba(${r}, ${g}, ${b}, 0) 100%
      );
      ${tabletAndUp(css`
        background-color: rgba(${r}, ${g}, ${b}, 1);
        :before {
          content: '';
          position: absolute;
          width: 30%;
          height: 100%;
          top: 0;
          left: -30%;
          background-image: linear-gradient(
            to left,
            rgba(${r}, ${g}, ${b}, 1) 0%,
            rgba(${r}, ${g}, ${b}, 0) 100%
          );
        }
      `)}
    `;
  }};

  @media ${mediaTabletAndUp} {
    gap: ${props => (props.$textScaleFactor === 1 ? '0.5rem' : '1rem')};
    position: inherit;
    width: 100%;
    bottom: auto;
    align-items: flex-start;
    padding: 4rem 4.25rem 2rem;
  }

  ${extraLargeScreens(css`
    padding: 4rem 5.5rem 2rem;
  `)}
`;

const StyledHeroElementHeadline = styled(HeroElementHeadline)<{
  $textScaleFactor: number;
}>`
  --headline-scale-factor: 1.2;

  &&& {
    ${({$textScaleFactor}) =>
      desktopAndUp(css`
        font-size: calc(
          ${FONT_SIZES.px14} * var(--headline-scale-factor) *
            ${$textScaleFactor} + 0.1vw
        );
      `)};

    ${({$textScaleFactor}) =>
      extraLargeScreens(css`
        font-size: calc(
          ${FONT_SIZES.px16} * var(--headline-scale-factor) *
            ${$textScaleFactor} + 0.1vw
        );
      `)};
  }
`;

const StyledHeroElementBodyText = styled(HeroElementBodyText)<{
  $textScaleFactor: number;
}>`
  &&& {
    line-height: 1.5;

    ${({$textScaleFactor}) =>
      desktopAndUp(css`
        font-size: calc(${FONT_SIZES.px14} * ${$textScaleFactor} + 0.1vw);
      `)};

    ${({$textScaleFactor}) =>
      extraLargeScreens(css`
        font-size: calc(${FONT_SIZES.px16} * ${$textScaleFactor} + 0.1vw);
      `)};
  }
`;

const StyledButton = styled(Button)<{$textScaleFactor: number}>`
  padding: 11px 40px;
  font-size: ${FONT_SIZES.px12};

  &&&& {
    margin-top: 1rem;
    height: auto;
  }

  --button-scale-factor: 0.8;
  ${({$textScaleFactor}) =>
    desktopAndUp(css`
      &&&& {
        font-size: calc(
          ${FONT_SIZES.px12} * var(--button-scale-factor) * ${$textScaleFactor} +
            0.1vw
        );
      }
    `)};

  ${({$textScaleFactor}) =>
    extraLargeScreens(css`
      &&&& {
        font-size: calc(
          ${FONT_SIZES.px14} * var(--button-scale-factor) * ${$textScaleFactor} +
            0.1vw
        );
      }
    `)};

  ${tabletAndUp(css`
    &&&& {
      width: auto;
      margin-top: 2rem;
    }
  `)}
`;
